import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from './TourContext';
import './TourGuia.css';

// Iconos SVG
const TourIcons = {
  wave: (
    // Estrella/bienvenida — limpia y reconocible
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  email: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  social: (
    // Dos personas — representa "acceso con red social"
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  building: (
    // Edificio/empresa
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 21V9h6v12" />
      <path d="M9 3v6M15 3v6" />
      <path d="M3 9h18" />
    </svg>
  ),
  warehouse: (
    // Almacén — frontón + puerta grande
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20V9.5L12 3l10 6.5V20H2z" />
      <rect x="8" y="13" width="8" height="7" />
      <line x1="12" y1="13" x2="12" y2="20" />
    </svg>
  ),
  post: (
    // Megáfono/publicación
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11l19-9-9 19-2-8-8-2z" />
    </svg>
  ),
  calendar: (
    // Calendario — reserva
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
};

const PADDING = 10;
const TOOLTIP_W = 320;
const TOOLTIP_H = 180; // estimado
const MOBILE_BP = 767;

function esMobile() {
  return window.innerWidth <= MOBILE_BP;
}

function obtenerRect(selector) {
  if (!selector) return null;
  // Recorre todos los matches y devuelve el primero que sea visible
  const els = document.querySelectorAll(selector);
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return r;
  }
  return null;
}

function calcularTooltipPos(rect, posicion, tooltipRef) {
  const tw = tooltipRef.current?.offsetWidth || TOOLTIP_W;
  const th = tooltipRef.current?.offsetHeight || TOOLTIP_H;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margen = 16;

  // En mobile: siempre bottom-sheet (no cubre el elemento resaltado)
  if (vw <= MOBILE_BP) {
    return {
      top: vh - th - margen,
      left: margen,
      clase: 'mobile-sheet',
    };
  }

  if (!rect || posicion === 'center') {
    return {
      top: vh / 2 - th / 2,
      left: vw / 2 - tw / 2,
      clase: 'centro',
    };
  }

  let top, left, clase;

  if (posicion === 'bottom') {
    top = rect.bottom + PADDING + 10;
    left = rect.left;
    clase = 'desde-bottom';
  } else if (posicion === 'top') {
    top = rect.top - th - PADDING - 10;
    left = rect.left;
    clase = 'desde-top';
  } else if (posicion === 'right') {
    top = rect.top;
    left = rect.right + PADDING + 10;
    clase = 'desde-right';
  } else if (posicion === 'left') {
    top = rect.top;
    left = rect.left - tw - PADDING - 10;
    clase = 'desde-left';
  } else {
    top = rect.bottom + PADDING + 10;
    left = rect.left;
    clase = 'desde-bottom';
  }

  // Ajuste para no salir de pantalla
  if (left + tw > vw - margen) left = vw - tw - margen;
  if (left < margen) left = margen;
  if (top + th > vh - margen) top = vh - th - margen;
  if (top < margen) top = margen;

  return { top, left, clase };
}

const TourGuia = () => {
  const { tourActivo, pasoActual, pasos, siguientePaso, anteriorPaso, terminarTour } = useTour();
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, clase: 'centro' });
  const tooltipRef = useRef(null);
  const rafRef = useRef(null);

  const paso = pasos[pasoActual];

  const recalcular = useCallback(() => {
    if (!tourActivo || !paso) return;
    const r = obtenerRect(paso.selector);
    setRect(r);
    setTooltipPos(calcularTooltipPos(r, paso.posicion, tooltipRef));
  }, [tourActivo, paso]);

  // Recalcular posición cuando cambia el paso o hay scroll/resize
  useEffect(() => {
    if (!tourActivo) return;

    // Intentar varias veces porque el DOM puede tardar en actualizarse (ej: modal abriéndose)
    let intentos = 0;
    let clicked = false; // evitar doble click por paso
    let scrolled = false; // evitar doble scroll por paso

    // Helper: primer elemento visible que coincide con el selector
    const primerVisible = (selector) => {
      if (!selector) return null;
      const els = document.querySelectorAll(selector);
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return el;
      }
      return null;
    };

    const reintentar = () => {
      // Si el paso tiene selector, hacer scroll al elemento una sola vez
      if (paso?.selector && !scrolled) {
        const el = primerVisible(paso.selector);
        if (el) {
          scrolled = true;
          // Scroll mínimo para hacer visible el elemento sin taparlo con el navbar
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
          // Esperar a que termine el scroll antes de calcular posición
          setTimeout(recalcular, 400);
        }
      }
      recalcular();
      // Si el paso requiere abrir su vista automáticamente, hacemos click una sola vez
      if (paso?.autoClick && !clicked && paso.selector) {
        const el = primerVisible(paso.selector);
        if (el) {
          clicked = true;
          el.click();
        }
      }
      intentos++;
      if (intentos < 8) {
        rafRef.current = setTimeout(reintentar, 150);
      }
    };
    reintentar();

    window.addEventListener('resize', recalcular);
    window.addEventListener('scroll', recalcular, true);

    return () => {
      window.removeEventListener('resize', recalcular);
      window.removeEventListener('scroll', recalcular, true);
      clearTimeout(rafRef.current);
    };
  }, [tourActivo, pasoActual, recalcular]);

  if (!tourActivo || !paso) return null;

  const esUltimo = pasoActual === pasos.length - 1;
  const esPrimero = pasoActual === 0;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Spotlight: coordenadas del agujero
  const sx = rect ? rect.left - PADDING : 0;
  const sy = rect ? rect.top - PADDING : 0;
  const sw = rect ? rect.width + PADDING * 2 : 0;
  const sh = rect ? rect.height + PADDING * 2 : 0;

  const overlay = (
    <div
      className="tour-root"
      key={pasoActual}
      style={{ zIndex: 2147483647, isolation: 'isolate' }}
    >
      {/* SVG oscuro con agujero */}
      <svg
        className="tour-overlay"
        viewBox={`0 0 ${vw} ${vh}`}
        preserveAspectRatio="none"
        onClick={terminarTour}
        aria-hidden="true"
      >
        <defs>
          <mask id="tourMask">
            <rect x="0" y="0" width={vw} height={vh} fill="white" />
            {rect && (
              <rect
                x={sx}
                y={sy}
                width={sw}
                height={sh}
                rx="10"
                ry="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width={vw}
          height={vh}
          fill="rgba(0,0,0,0.62)"
          mask="url(#tourMask)"
        />
      </svg>

      {/* Borde brillante alrededor del elemento */}
      {rect && (
        <div
          className="tour-spotlight-border"
          style={{
            top: sy,
            left: sx,
            width: sw,
            height: sh,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip ${tooltipPos.clase}`}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
        onClick={e => e.stopPropagation()}
      >
        <div className="tour-card">
          {/* Banda superior con icono y título */}
          <div className="tour-card-header">
            <div className="tour-paso-badge">
              {pasoActual + 1}
            </div>
            <div className="tour-titulo-group">
              {paso.icono && (
                <span className="tour-icono" title={paso.icono}>
                  {TourIcons[paso.icono] || null}
                </span>
              )}
              <span className="tour-titulo">{paso.titulo}</span>
            </div>
            <button className="tour-cerrar" onClick={terminarTour} title="Saltar tour">
              ×
            </button>
          </div>

          {/* Cuerpo */}
          <div className="tour-card-body">
            <p className="tour-descripcion">{paso.descripcion}</p>

            <div className="tour-divider" />

            <div className="tour-footer">
              {/* Dots de progreso */}
              <div className="tour-dots">
                {pasos.map((_, i) => (
                  <div key={i} className={`tour-dot${i === pasoActual ? ' activo' : ''}`} />
                ))}
              </div>

              {/* Botones */}
              <div className="tour-botones">
                {!esPrimero && (
                  <button className="tour-btn-prev" onClick={anteriorPaso}>
                    ← Atrás
                  </button>
                )}
                {esPrimero && (
                  <button className="tour-saltar" onClick={terminarTour}>
                    Saltar
                  </button>
                )}
                <button className="tour-btn-next" onClick={esUltimo ? terminarTour : siguientePaso}>
                  {esUltimo ? '¡Entendido! ✓' : 'Siguiente →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default TourGuia;
